class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.setupComplete = false;
  }

  connectedCallback() {
    // Element is now in DOM
    if (this.setupComplete) return;
    this.setupComplete = true;

    // Find elements within custom element
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    // For top drawer, predictive search results are outside custom element
    const drawer = this.closest(".drawer--top");
    if (drawer) {
      this.predictiveSearchResults = drawer.querySelector(
        "[data-predictive-search]"
      );
      this.statusElement = drawer.querySelector(".predictive-search-status");
    } else {
      this.predictiveSearchResults = this.querySelector(
        "[data-predictive-search]"
      );
      this.statusElement = this.querySelector(".predictive-search-status");
    }

    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");

    if (!this.input) {
      console.error("PredictiveSearch: No search input found");
      return;
    }

    // Add debounced input listener
    this.input.addEventListener("input", (event) => {
      // Clear existing timeout
      if (this.inputTimeout) {
        clearTimeout(this.inputTimeout);
      }

      // Set new timeout
      this.inputTimeout = setTimeout(() => {
        this.onChange(event);
      }, 300);
    });

    // Add form listeners
    if (this.input.form) {
      this.input.form.addEventListener("reset", this.onFormReset.bind(this));
      this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
    }

    // Add other event listeners
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    // Setup clear button for top drawer (reuse drawer variable from above)
    if (drawer) {
      const clearButton = drawer.querySelector("[data-clear-search-top]");
      if (clearButton) {
        clearButton.addEventListener("click", () => {
          this.input.value = "";
          this.input.focus();
          clearButton.classList.add("hidden");
          this.close(true);
        });
      }
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    // Handle reset button if exists (for non-drawer forms)
    if (this.resetButton) {
      const resetIsHidden = this.resetButton.classList.contains("hidden");
      if (this.input.value.length > 0 && resetIsHidden) {
        this.resetButton.classList.remove("hidden");
      } else if (this.input.value.length === 0 && !resetIsHidden) {
        this.resetButton.classList.add("hidden");
      }
    }

    // Handle clear button visibility for top drawer
    const topDrawer = this.closest(".drawer--top");
    if (topDrawer) {
      const clearButton = topDrawer.querySelector("[data-clear-search-top]");
      if (clearButton) {
        if (this.input.value.length > 0) {
          clearButton.classList.remove("hidden");
        } else {
          clearButton.classList.add("hidden");
        }
      }
    }

    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();

    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm && this.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.input.value = "";
      this.input.focus();
      this.closeResults(true);

      // Handle reset button if it exists (for non-drawer search forms)
      if (this.resetButton && this.toggleResetButton) {
        this.toggleResetButton();
      }
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      // For top drawer, check if focus is still within the drawer
      const drawer = this.closest(".drawer--top");
      if (drawer) {
        // Don't close if focus is still within the drawer
        if (drawer.contains(document.activeElement)) {
          return;
        }
      }

      // For regular search, check if focus is still within predictive search
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) {
      this.statusElement.textContent = "";
    }

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const predictiveSearchUrl =
      typeof routes !== "undefined" && routes.predictive_search_url
        ? routes.predictive_search_url
        : "/search/suggest";
    const url = `${predictiveSearchUrl}?q=${encodeURIComponent(
      searchTerm
    )}&section_id=predictive-search`;

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) {
      console.error(
        "PredictiveSearch: predictiveSearchResults element not found"
      );
      return;
    }

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    // Remove d-none from predictive search container
    this.predictiveSearchResults.classList.remove("d-none");

    this.setLiveRegionResults();
    this.open();

    // Toggle trending/results sections for top drawer
    this.toggleDrawerSections(true);
    this.updateViewAllLink();
  }

  toggleDrawerSections(showResults) {
    const drawer = this.closest(".drawer--top");
    if (!drawer) return;

    const trendingSection = drawer.querySelector("[data-trending-section]");
    const resultsSection = drawer.querySelector("[data-results-section]");

    if (trendingSection && resultsSection) {
      if (showResults) {
        trendingSection.classList.add("d-none");
        resultsSection.classList.remove("d-none");
      } else {
        trendingSection.classList.remove("d-none");
        resultsSection.classList.add("d-none");
      }
    }
  }

  updateViewAllLink() {
    const drawer = this.closest(".drawer--top");
    if (!drawer) return;

    const viewAllLink = drawer.querySelector("[data-view-all-link]");
    if (!viewAllLink) return;

    // Count total results - search within predictiveSearchResults element
    const resultsWrapper = this.predictiveSearchResults
      ? this.predictiveSearchResults.querySelector("#predictive-search-results")
      : null;

    if (!resultsWrapper) {
      viewAllLink.classList.add("d-none");
      return;
    }

    const productsList = resultsWrapper.querySelector(
      "#predictive-search-results-products-list"
    );
    const queriesList = resultsWrapper.querySelector(
      "#predictive-search-results-queries-list"
    );
    const pagesList = resultsWrapper.querySelector(
      "#predictive-search-results-pages-list"
    );

    const totalProducts = productsList
      ? productsList.querySelectorAll("li").length
      : 0;
    const totalQueries = queriesList
      ? queriesList.querySelectorAll("li").length
      : 0;
    const totalPages = pagesList ? pagesList.querySelectorAll("li").length : 0;
    const totalResults = totalProducts + totalQueries + totalPages;

    // Show "View All" link if there are any results
    if (totalResults > 0) {
      const linkText = `Tüm Sonuçları Göster (${totalResults})`;
      const linkStyle = viewAllLink.querySelector(".link-style");
      if (linkStyle) {
        linkStyle.textContent = linkText;
      } else {
        // If .link-style doesn't exist, set text directly
        viewAllLink.textContent = linkText;
      }

      // Get search URL from form action
      const form = this.querySelector("form");
      const baseUrl = form ? form.getAttribute("action") : "/search";
      const searchUrl = `${baseUrl}?q=${encodeURIComponent(this.searchTerm)}`;

      viewAllLink.href = searchUrl;

      // Remove old click listener if exists
      const newViewAllLink = viewAllLink.cloneNode(true);
      viewAllLink.parentNode.replaceChild(newViewAllLink, viewAllLink);

      // Add click listener to navigate properly
      newViewAllLink.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = searchUrl;
      });

      newViewAllLink.classList.remove("d-none");
    } else {
      viewAllLink.classList.add("d-none");
    }
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const countElement = this.querySelector(
      "[data-predictive-search-live-region-count-value]"
    );
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  getResultsMaxHeight() {
    const header = document.querySelector(".section-header");
    if (header) {
      this.resultsMaxHeight =
        window.innerHeight - header.getBoundingClientRect().bottom;
    } else {
      this.resultsMaxHeight = window.innerHeight - 100; // Default fallback
    }
    return this.resultsMaxHeight;
  }

  open() {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.maxHeight =
        this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute("open", true);
    if (this.input) {
      this.input.setAttribute("aria-expanded", true);
    }
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm && this.input) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    if (this.input) {
      this.input.setAttribute("aria-activedescendant", "");
      this.input.setAttribute("aria-expanded", false);
    }

    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.resultsMaxHeight = false;

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute("style");
      this.predictiveSearchResults.classList.add("d-none");
    }

    // Show trending section again when closing
    this.toggleDrawerSections(false);
  }
}

customElements.define("predictive-search", PredictiveSearch);
